import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { useDelegationFromSign } from '@helpers/delegate-helper';
import { TranslatePipe } from '@ngx-translate/core';
import { DestroyService } from '@services/destroy.service';
import { DataStorageService } from '@services/onchain/data-storage.service';
import { RelayService } from '@services/onchain/relay.service';
import { ProviderService } from '@services/provider.service';
import { DialogTitleComponent } from '@shared/components/dialog-title/dialog-title.component';
import { LoadingSmallComponent } from '@shared/components/loading-small/loading-small.component';
import { ScratchComponent } from '@shared/components/scratch/scratch.component';
import { ADDRESS_ZERO } from '@shared/constants/addresses/addresses.constant';
import { CHAIN_FIELDS, getChainByChainId } from '@shared/constants/chain-ids.constant';
import { MAIN_ROUTES } from '@shared/constants/routes.constant';
import { getDateInUTC } from '@shared/time-utils';
import { fromBytes } from '@shared/utils';
import { formatUnits } from 'ethers';
import { NGXLogger } from 'ngx-logger';
import { takeUntil, from, forkJoin } from 'rxjs';

export const STORAGE_DELEGATE_HIDE = 'TETU_GAME_DELEGATE_HIDE';
export const STORAGE_DELEGATE_EMPTY_PASS = 'STORAGE_DELEGATE_EMPTY_PASS';

@Component({
  selector: 'app-delegate-dialog',
  standalone: true,
  templateUrl: './delegate-dialog.html',
  styleUrls: ['./delegate-dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'app-window-responsive-background g-flex-column',
  },
  imports: [DialogTitleComponent, LoadingSmallComponent, ScratchComponent, TranslatePipe],
})
export class DelegateDialogComponent implements OnInit {
  customWidth = 990;
  forceOpen = false;

  @HostBinding('style.width.px')
  private get modalWidth() {
    return this.customWidth;
  }

  account: string;
  chainId: number;
  loading = true;
  processing = false;
  errorMsg = '';

  delegator = ADDRESS_ZERO;
  nonce = 0n;
  delegatorDeadline = 0n;
  delegatorBalance = 0;
  accountBalance = 0;

  encryptedDelegatePrivateKey: string | null = null;

  constructor(
    @Inject(DIALOG_DATA)
    public data: {
      customWidth: number;
      forceOpen: boolean;
    } = {
      customWidth: 990,
      forceOpen: false,
    },
    private dialogRef: DialogRef<null, DelegateDialogComponent>,
    private destroy$: DestroyService,
    private providerService: ProviderService,
    private changeDetectorRef: ChangeDetectorRef,
    private relayService: RelayService,
    private dataStorageService: DataStorageService,
    private logger: NGXLogger,
    private router: Router,
  ) {
    this.customWidth = data.customWidth;
    this.forceOpen = data.forceOpen;
  }

  ngOnInit() {
    this.providerService.subscribeOnAccountAndNetwork(
      this.destroy$,
      this.changeDetectorRef,
      account => {
        this.account = account;
        this.init();
      },
      chainId => {
        this.chainId = chainId;
        this.init();
      },
    );
  }

  close(): void {
    this.dialogRef.close(null);
  }

  storageDelegatePkKey() {
    return 'TETU_GAME_DELEGATE_PK_' + this.account;
  }

  init() {
    if (this.account && this.chainId) {
      this.loading = true;

      forkJoin([
        this.relayService.userInfo$(this.chainId, this.account),
        this.dataStorageService.getData$(this.chainId, this.account),
      ])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([userInfo, data]) => {
          this.encryptedDelegatePrivateKey = fromBytes(data as string);

          this.delegator = userInfo.delegator;
          this.nonce = userInfo.nonce;
          this.delegatorDeadline = userInfo.delegatorDeadline;

          this.logger.trace('this.delegator', this.delegator);
          this.logger.trace('this.nonce', this.nonce);
          this.logger.trace('this.delegatorDeadline', this.delegatorDeadline);

          this.loadDelegatorBalance();
          this.loadAccountBalance();

          this.loading = false;

          this.changeDetectorRef.detectChanges();
        });
    }
  }

  loadDelegatorBalance() {
    if (this.delegator !== ADDRESS_ZERO) {
      from(this.providerService.getProviderForRead().provider.getBalance(this.delegator))
        .pipe(takeUntil(this.destroy$))
        .subscribe(balance => {
          this.logger.trace('delegator balance', +formatUnits(balance));
          this.delegatorBalance = +formatUnits(balance);
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  loadAccountBalance() {
    from(this.providerService.getProviderForRead().provider.getBalance(this.account))
      .pipe(takeUntil(this.destroy$))
      .subscribe(balance => {
        this.logger.trace('account balance', balance);
        this.accountBalance = +formatUnits(balance);
        this.changeDetectorRef.detectChanges();
      });
  }

  ///////////////////////////////// MAIN LOGIC /////////////////////////////////

  isDelegatorOutOfDate() {
    return this.delegatorDeadline < BigInt((getDateInUTC().getTime() / 1000).toFixed(0));
  }

  symbolNetworkCoin() {
    return getChainByChainId(this.chainId)[CHAIN_FIELDS.TOKEN_NAME] ?? '???';
  }

  isEnoughBalanceOnDelegator() {
    return this.delegatorBalance > (getChainByChainId(this.chainId)[CHAIN_FIELDS.COINS_FOR_DELEGATION] ?? 1) / 10;
  }

  useDelegation() {
    this.errorMsg = '';

    if (!this.encryptedDelegatePrivateKey) {
      this.errorMsg = 'No encrypted PK!';

      return;
    }

    useDelegationFromSign(
      this.account,
      this.delegator,
      this.encryptedDelegatePrivateKey,
      this.destroy$,
      this.providerService,
      this.changeDetectorRef,
      this.logger,
      msg => {
        this.errorMsg = msg;
      },
      () => {
        this.close();
      },
    );
  }

  goToSettings() {
    this.router.navigate([MAIN_ROUTES.DELEGATE]);
    this.close();
  }
}
